import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import DashboardTabs from '../components/dashboard/DashboardTabs';
import { dashboardTabs } from '../components/dashboard/dashboardMockData';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import {
    createLocation,
    fetchLocation,
    listLocations,
    updateLocation
} from '../services/locations';
import {
    createSharedUser,
    deleteSharedUser,
    listSharedUsers
} from '../services/sharedUsers';
import {
    getClinicalParameters,
    updateClinicalParameters
} from '../services/clinicalParameters';

const primaryButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#D63731',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#BF2F2A',
        boxShadow: 'none'
    }
};

const secondaryButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px'
    }
};

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const dialogContentSx = {
    px: 3,
    pb: 1,
    pt: 0.5,
    display: 'grid',
    gap: 12
};

const dialogActionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1
};

const emptyForm = {
    uuid: '',
    name: '',
    content: '',
    contacts: [{ name: '', phone: '' }],
    isLegacyText: false
};

const emptyContact = () => ({
    name: '',
    phone: ''
});

const normalizeLocationContacts = (contacts) => {
    if (!Array.isArray(contacts) || contacts.length === 0) {
        return [emptyContact()];
    }

    const normalized = contacts
        .map((item) => ({
            name: typeof item?.name === 'string' ? item.name : '',
            phone: typeof item?.phone === 'string' ? item.phone : ''
        }))
        .filter((item) => item.name.trim() || item.phone.trim());

    return normalized.length ? normalized : [emptyContact()];
};

const isLocationLegacyText = (location) =>
    !Array.isArray(location?.contacts) && typeof location?.content === 'string' && location.content.trim() !== '';

const settingsTabs = [
    { key: 'sos-locations', label: 'Localizações SOS' },
    { key: 'shared-accounts', label: 'Contas partilhadas' },
    { key: 'notifications', label: 'Alertas e Notificações' }
];

const settingsTabSx = {
    minHeight: 44,
    px: 2.2,
    borderRadius: '14px',
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '1.5px',
    bgcolor: '#FFFFFF',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '1.5px',
        boxShadow: 'none'
    }
};

const settingsTabActiveSx = {
    ...settingsTabSx,
    color: '#D63731',
    borderColor: '#D63731',
    bgcolor: 'rgba(214, 55, 49, 0.05)',
    '&:hover': {
        bgcolor: 'rgba(214, 55, 49, 0.08)',
        borderColor: '#D63731',
        borderWidth: '1.5px',
        boxShadow: 'none'
    }
};

const nonAlertMeasurements = [
    'Temperatura Corporal',
    'Peso',
    'Exercício',
    'Mood / Sobrecarga'
];

const clinicalParametersDefaults = {
    heartrate: {
        threshold: 220
    },
    blood_pressure: {
        severe: {
            systolic: 180,
            diastolic: 120
        },
        stage_2: {
            systolic: 140,
            diastolic: 90
        }
    },
    blood_oxygen: {
        threshold: 90
    },
    blood_sugar: {
        low: 70,
        high: 160
    }
};

const normalizeClinicalParameters = (incoming = {}) => ({
    heartrate: {
        threshold:
            incoming?.heartrate?.threshold ?? clinicalParametersDefaults.heartrate.threshold
    },
    blood_pressure: {
        severe: {
            systolic:
                incoming?.blood_pressure?.severe?.systolic ??
                clinicalParametersDefaults.blood_pressure.severe.systolic,
            diastolic:
                incoming?.blood_pressure?.severe?.diastolic ??
                clinicalParametersDefaults.blood_pressure.severe.diastolic
        },
        stage_2: {
            systolic:
                incoming?.blood_pressure?.stage_2?.systolic ??
                clinicalParametersDefaults.blood_pressure.stage_2.systolic,
            diastolic:
                incoming?.blood_pressure?.stage_2?.diastolic ??
                clinicalParametersDefaults.blood_pressure.stage_2.diastolic
        }
    },
    blood_oxygen: {
        threshold:
            incoming?.blood_oxygen?.threshold ?? clinicalParametersDefaults.blood_oxygen.threshold
    },
    blood_sugar: {
        low: incoming?.blood_sugar?.low ?? clinicalParametersDefaults.blood_sugar.low,
        high: incoming?.blood_sugar?.high ?? clinicalParametersDefaults.blood_sugar.high
    }
});

const buildClinicalParametersForm = (parameters = clinicalParametersDefaults) => {
    const normalized = normalizeClinicalParameters(parameters);

    return {
        heartrate: {
            threshold: String(normalized.heartrate.threshold)
        },
        blood_pressure: {
            severe: {
                systolic: String(normalized.blood_pressure.severe.systolic),
                diastolic: String(normalized.blood_pressure.severe.diastolic)
            },
            stage_2: {
                systolic: String(normalized.blood_pressure.stage_2.systolic),
                diastolic: String(normalized.blood_pressure.stage_2.diastolic)
            }
        },
        blood_oxygen: {
            threshold: String(normalized.blood_oxygen.threshold)
        },
        blood_sugar: {
            low: String(normalized.blood_sugar.low),
            high: String(normalized.blood_sugar.high)
        }
    };
};

const buildClinicalParametersPayload = (form) => ({
    heartrate: {
        threshold: Number(form.heartrate.threshold)
    },
    blood_pressure: {
        severe: {
            systolic: Number(form.blood_pressure.severe.systolic),
            diastolic: Number(form.blood_pressure.severe.diastolic)
        },
        stage_2: {
            systolic: Number(form.blood_pressure.stage_2.systolic),
            diastolic: Number(form.blood_pressure.stage_2.diastolic)
        }
    },
    blood_oxygen: {
        threshold: Number(form.blood_oxygen.threshold)
    },
    blood_sugar: {
        low: Number(form.blood_sugar.low),
        high: Number(form.blood_sugar.high)
    }
});

const Settings = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [activeSettingsTab, setActiveSettingsTab] = useState('sos-locations');
    const [sharedUsers, setSharedUsers] = useState([]);
    const [sharedUsersLoading, setSharedUsersLoading] = useState(false);
    const [sharedUsersError, setSharedUsersError] = useState('');
    const [sharedUserDialogOpen, setSharedUserDialogOpen] = useState(false);
    const [sharedUserEmail, setSharedUserEmail] = useState('');
    const [sharedUserSubmitting, setSharedUserSubmitting] = useState(false);
    const [sharedUserDialogError, setSharedUserDialogError] = useState('');
    const [sharedUserDeleteTarget, setSharedUserDeleteTarget] = useState(null);
    const [sharedUserDeleting, setSharedUserDeleting] = useState(false);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedLocationUuid, setSelectedLocationUuid] = useState('');
    const [selectedLocationDetails, setSelectedLocationDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [dialogSubmitting, setDialogSubmitting] = useState(false);
    const [dialogError, setDialogError] = useState('');
    const [dialogForm, setDialogForm] = useState(emptyForm);
    const [success, setSuccess] = useState('');
    const [clinicalParameters, setClinicalParameters] = useState(
        normalizeClinicalParameters()
    );
    const [clinicalParametersForm, setClinicalParametersForm] = useState(
        buildClinicalParametersForm(clinicalParametersDefaults)
    );
    const [clinicalParametersLoading, setClinicalParametersLoading] = useState(false);
    const [clinicalParametersError, setClinicalParametersError] = useState('');
    const [clinicalParametersSaving, setClinicalParametersSaving] = useState(false);
    const [clinicalParametersEditing, setClinicalParametersEditing] = useState(false);
    const [clinicalParametersLastUpdate, setClinicalParametersLastUpdate] = useState('');

    const handleTabClick = (tab) => {
        if (tab === 'Início') {
            navigate('/');
            return;
        }

        if (tab === 'Agendamentos') {
            navigate('/appointments');
            return;
        }

        if (tab === 'Equipa Técnica') {
            navigate('/doctors');
            return;
        }

        if (tab === 'Cuidadores e Pacientes') {
            navigate('/caregivers');
            return;
        }

        if (tab === 'Documentos') {
            navigate('/documents');
        }
    };

    useEffect(() => {
        const loadLocations = async () => {
            if (!user.selectedRole) {
                return;
            }

            setLoading(true);
            setError('');

            try {
                const response = await listLocations({
                    userRoleUuid: user.selectedRole,
                    paginationLimit: 50
                });

                const list = Array.isArray(response?.data) ? response.data : [];
                setLocations(list);
                setSelectedLocationUuid((prev) =>
                    prev && list.some((item) => item.uuid === prev) ? prev : list[0]?.uuid || ''
                );
            } catch (err) {
                setLocations([]);
                setError(err?.response?.data?.description || 'Não foi possível carregar as localizações.');
            } finally {
                setLoading(false);
            }
        };

        void loadLocations();
    }, [user.selectedRole]);

    useEffect(() => {
        const loadLocationDetails = async () => {
            if (!user.selectedRole || !selectedLocationUuid) {
                setSelectedLocationDetails(null);
                return;
            }

            setLoadingDetails(true);
            setDetailsError('');

            try {
                const response = await fetchLocation({
                    userRoleUuid: user.selectedRole,
                    locationUuid: selectedLocationUuid
                });
                setSelectedLocationDetails(response?.data || null);
            } catch (err) {
                setSelectedLocationDetails(null);
                setDetailsError(
                    err?.response?.data?.description || 'Não foi possível carregar os detalhes da localização.'
                );
            } finally {
                setLoadingDetails(false);
            }
        };

        void loadLocationDetails();
    }, [selectedLocationUuid, user.selectedRole]);

    useEffect(() => {
        const loadSharedUsers = async () => {
            if (!user.selectedRole) {
                return;
            }

            setSharedUsersLoading(true);
            setSharedUsersError('');

            try {
                const response = await listSharedUsers({
                    userRoleUuid: user.selectedRole,
                    paginationLimit: 50
                });
                setSharedUsers(Array.isArray(response?.data) ? response.data : []);
            } catch (err) {
                setSharedUsers([]);
                setSharedUsersError(
                    err?.response?.data?.description || 'Não foi possível carregar as contas partilhadas.'
                );
            } finally {
                setSharedUsersLoading(false);
            }
        };

        void loadSharedUsers();
    }, [user.selectedRole]);

    useEffect(() => {
        const loadClinicalParameters = async () => {
            if (!user.selectedRole || activeSettingsTab !== 'notifications') {
                return;
            }

            setClinicalParametersLoading(true);
            setClinicalParametersError('');

            try {
                const response = await getClinicalParameters({
                    userRoleUuid: user.selectedRole
                });

                const normalized = normalizeClinicalParameters(response?.data?.parameters || {});
                setClinicalParameters(normalized);
                setClinicalParametersForm(buildClinicalParametersForm(normalized));
                setClinicalParametersLastUpdate(response?.data?.last_update || '');
            } catch (err) {
                const fallback = normalizeClinicalParameters();
                setClinicalParameters(fallback);
                setClinicalParametersForm(buildClinicalParametersForm(fallback));
                setClinicalParametersError(
                    err?.response?.data?.description ||
                        'Não foi possível carregar os parâmetros clínicos.'
                );
            } finally {
                setClinicalParametersLoading(false);
            }
        };

        void loadClinicalParameters();
    }, [activeSettingsTab, user.selectedRole]);

    const selectedLocationSummary = useMemo(
        () => locations.find((item) => item.uuid === selectedLocationUuid) || null,
        [locations, selectedLocationUuid]
    );

    const biometricAlertRules = useMemo(
        () => [
            {
                key: 'blood-pressure',
                title: 'Pressão Arterial',
                description:
                    'Gera alertas automáticos quando a tensão arterial entra em patamares de hipertensão avançada.',
                priority: 'Média / Alta',
                conditions: [
                    {
                        path: ['blood_pressure', 'stage_2', 'systolic'],
                        label: 'Hipertensão Estágio 2 - Sistólica',
                        suffix: 'mmHg'
                    },
                    {
                        path: ['blood_pressure', 'stage_2', 'diastolic'],
                        label: 'Hipertensão Estágio 2 - Diastólica',
                        suffix: 'mmHg'
                    },
                    {
                        path: ['blood_pressure', 'severe', 'systolic'],
                        label: 'Hipertensão Severa - Sistólica',
                        suffix: 'mmHg'
                    },
                    {
                        path: ['blood_pressure', 'severe', 'diastolic'],
                        label: 'Hipertensão Severa - Diastólica',
                        suffix: 'mmHg'
                    }
                ]
            },
            {
                key: 'heartrate',
                title: 'Frequência Cardíaca',
                description:
                    'O alerta é disparado com base na idade da pessoa e no valor de frequência cardíaca registado.',
                priority: 'Alta',
                conditions: [
                    {
                        path: ['heartrate', 'threshold'],
                        label: 'Frequência cardíaca máxima base',
                        suffix: 'bpm'
                    }
                ]
            },
            {
                key: 'blood-oxygen',
                title: 'Oxigénio no Sangue',
                description:
                    'A saturação baixa gera alerta imediato para acompanhamento clínico.',
                priority: 'Alta',
                conditions: [
                    {
                        path: ['blood_oxygen', 'threshold'],
                        label: 'Saturação mínima',
                        suffix: '%'
                    }
                ]
            },
            {
                key: 'blood-sugar',
                title: 'Glicemia',
                description:
                    'São gerados alertas para valores acima ou abaixo dos intervalos definidos no backend.',
                priority: 'Alta',
                conditions: [
                    {
                        path: ['blood_sugar', 'high'],
                        label: 'Glicemia alta',
                        suffix: 'mg/dL'
                    },
                    {
                        path: ['blood_sugar', 'low'],
                        label: 'Glicemia baixa',
                        suffix: 'mg/dL'
                    }
                ]
            }
        ],
        []
    );

    const reloadLocations = async (preferredUuid = null) => {
        if (!user.selectedRole) {
            return;
        }

        const response = await listLocations({
            userRoleUuid: user.selectedRole,
            paginationLimit: 50
        });
        const list = Array.isArray(response?.data) ? response.data : [];
        setLocations(list);
        const nextUuid =
            preferredUuid && list.some((item) => item.uuid === preferredUuid)
                ? preferredUuid
                : list[0]?.uuid || '';
        setSelectedLocationUuid(nextUuid);
    };

    const reloadSharedUsers = async () => {
        if (!user.selectedRole) {
            return;
        }

        const response = await listSharedUsers({
            userRoleUuid: user.selectedRole,
            paginationLimit: 50
        });
        setSharedUsers(Array.isArray(response?.data) ? response.data : []);
    };

    const handleOpenCreateDialog = () => {
        setDialogMode('create');
        setDialogError('');
        setDialogForm({
            ...emptyForm,
            contacts: [emptyContact()]
        });
        setDialogOpen(true);
    };

    const handleOpenEditDialog = () => {
        if (!selectedLocationDetails) {
            return;
        }

        setDialogMode('edit');
        setDialogError('');
        setDialogForm({
            uuid: selectedLocationDetails.uuid || '',
            name: selectedLocationDetails.name || '',
            content: selectedLocationDetails.content || '',
            contacts: normalizeLocationContacts(selectedLocationDetails.contacts),
            isLegacyText: isLocationLegacyText(selectedLocationDetails)
        });
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        if (dialogSubmitting) {
            return;
        }

        setDialogOpen(false);
        setDialogError('');
        setDialogForm({
            ...emptyForm,
            contacts: [emptyContact()]
        });
    };

    const handleLocationContactChange = (index, field, value) => {
        setDialogForm((prev) => ({
            ...prev,
            contacts: prev.contacts.map((contact, contactIndex) =>
                contactIndex === index
                    ? {
                          ...contact,
                          [field]: value
                      }
                    : contact
            )
        }));
    };

    const handleAddLocationContact = () => {
        setDialogForm((prev) => ({
            ...prev,
            contacts: [...prev.contacts, emptyContact()]
        }));
    };

    const handleRemoveLocationContact = (index) => {
        setDialogForm((prev) => {
            const nextContacts = prev.contacts.filter((_, contactIndex) => contactIndex !== index);

            return {
                ...prev,
                contacts: nextContacts.length ? nextContacts : [emptyContact()]
            };
        });
    };

    const handleSaveDialog = async () => {
        if (!user.selectedRole) {
            return;
        }

        const trimmedName = dialogForm.name.trim();
        const trimmedContent = dialogForm.content.trim();
        const normalizedContacts = dialogForm.contacts
            .map((contact) => ({
                name: contact.name.trim(),
                phone: contact.phone.trim()
            }))
            .filter((contact) => contact.name || contact.phone);

        if (!trimmedName) {
            setDialogError('O nome da localização é obrigatório.');
            return;
        }

        if (dialogForm.isLegacyText) {
            if (!trimmedContent) {
                setDialogError('Os contactos de emergência são obrigatórios.');
                return;
            }
        } else if (normalizedContacts.length === 0) {
            setDialogError('Adiciona pelo menos um contacto com nome ou telefone.');
            return;
        }

        setDialogSubmitting(true);
        setDialogError('');

        try {
            if (dialogMode === 'create') {
                const response = await createLocation({
                    userRoleUuid: user.selectedRole,
                    payload: {
                        name: trimmedName,
                        format: 'PLAIN',
                        content: '',
                        contacts: normalizedContacts
                    }
                });
                const createdUuid = response?.data?.uuid || null;
                await reloadLocations(createdUuid);
                setSuccess('Localização criada com sucesso.');
            } else {
                const payload = dialogForm.isLegacyText
                    ? {
                          location: dialogForm.uuid,
                          name: trimmedName,
                          format: 'PLAIN',
                          content: trimmedContent
                      }
                    : {
                          location: dialogForm.uuid,
                          name: trimmedName,
                          contacts: normalizedContacts
                      };

                await updateLocation({
                    userRoleUuid: user.selectedRole,
                    payload
                });
                await reloadLocations(dialogForm.uuid);
                setSuccess('Localização atualizada com sucesso.');
            }

            setDialogOpen(false);
            setDialogForm({
                ...emptyForm,
                contacts: [emptyContact()]
            });
        } catch (err) {
            setDialogError(err?.response?.data?.description || 'Não foi possível guardar a localização.');
        } finally {
            setDialogSubmitting(false);
        }
    };
    const handleOpenSharedUserDialog = () => {
        setSharedUserDialogError('');
        setSharedUserEmail('');
        setSharedUserDialogOpen(true);
    };

    const handleCloseSharedUserDialog = () => {
        if (sharedUserSubmitting) {
            return;
        }

        setSharedUserDialogOpen(false);
        setSharedUserDialogError('');
        setSharedUserEmail('');
    };

    const handleCreateSharedUser = async () => {
        if (!user.selectedRole) {
            return;
        }

        const normalizedEmail = sharedUserEmail.trim().toLowerCase();
        if (!normalizedEmail) {
            setSharedUserDialogError('O email é obrigatório.');
            return;
        }

        setSharedUserSubmitting(true);
        setSharedUserDialogError('');

        try {
            await createSharedUser({
                userRoleUuid: user.selectedRole,
                email: normalizedEmail
            });
            await reloadSharedUsers();
            setSharedUserDialogOpen(false);
            setSharedUserEmail('');
            setSuccess('Conta partilhada criada com sucesso.');
        } catch (err) {
            setSharedUserDialogError(
                err?.response?.data?.description || 'Não foi possível criar a conta partilhada.'
            );
        } finally {
            setSharedUserSubmitting(false);
        }
    };

    const handleOpenDeleteSharedUserDialog = (target) => {
        setSharedUserDeleteTarget(target);
    };

    const handleCloseDeleteSharedUserDialog = () => {
        if (sharedUserDeleting) {
            return;
        }

        setSharedUserDeleteTarget(null);
    };

    const handleDeleteSharedUser = async () => {
        if (!user.selectedRole || !sharedUserDeleteTarget?.uuid) {
            return;
        }

        setSharedUserDeleting(true);
        setSharedUsersError('');

        try {
            await deleteSharedUser({
                userRoleUuid: user.selectedRole,
                userUuid: sharedUserDeleteTarget.uuid
            });
            await reloadSharedUsers();
            setSharedUserDeleteTarget(null);
            setSuccess('Conta partilhada desativada com sucesso.');
        } catch (err) {
            setSharedUsersError(
                err?.response?.data?.description || 'Não foi possível desativar a conta partilhada.'
            );
        } finally {
            setSharedUserDeleting(false);
        }
    };

    const handleClinicalParameterFieldChange = (path, value) => {
        setClinicalParametersForm((prev) => {
            const next = structuredClone(prev);
            let cursor = next;

            for (let index = 0; index < path.length - 1; index += 1) {
                cursor = cursor[path[index]];
            }

            cursor[path[path.length - 1]] = value.replace(/[^\d]/g, '');
            return next;
        });
    };

    const handleEnableClinicalParametersEdit = () => {
        setClinicalParametersEditing(true);
        setClinicalParametersError('');
        setClinicalParametersForm(buildClinicalParametersForm(clinicalParameters));
    };

    const handleCancelClinicalParametersEdit = () => {
        setClinicalParametersEditing(false);
        setClinicalParametersError('');
        setClinicalParametersForm(buildClinicalParametersForm(clinicalParameters));
    };

    const handleSaveClinicalParameters = async () => {
        if (!user.selectedRole) {
            return;
        }

        const allValues = [
            clinicalParametersForm.heartrate.threshold,
            clinicalParametersForm.blood_pressure.stage_2.systolic,
            clinicalParametersForm.blood_pressure.stage_2.diastolic,
            clinicalParametersForm.blood_pressure.severe.systolic,
            clinicalParametersForm.blood_pressure.severe.diastolic,
            clinicalParametersForm.blood_oxygen.threshold,
            clinicalParametersForm.blood_sugar.low,
            clinicalParametersForm.blood_sugar.high
        ];

        if (allValues.some((item) => item === '' || Number.isNaN(Number(item)))) {
            setClinicalParametersError('Todos os limites devem ter valores numéricos válidos.');
            return;
        }

        const payload = buildClinicalParametersPayload(clinicalParametersForm);

        setClinicalParametersSaving(true);
        setClinicalParametersError('');

        try {
            await updateClinicalParameters({
                userRoleUuid: user.selectedRole,
                parameters: payload
            });

            const normalized = normalizeClinicalParameters(payload);
            setClinicalParameters(normalized);
            setClinicalParametersForm(buildClinicalParametersForm(normalized));
            setClinicalParametersEditing(false);
            setClinicalParametersLastUpdate(new Date().toISOString());
            setSuccess('Parâmetros clínicos atualizados com sucesso.');
        } catch (err) {
            setClinicalParametersError(
                err?.response?.data?.description ||
                    'Não foi possível guardar os parâmetros clínicos.'
            );
        } finally {
            setClinicalParametersSaving(false);
        }
    };

    return (
        <Box
            sx={{
                px: { xs: 1.5, md: 2 },
                pb: { xs: 1.5, md: 2 },
                pt: 0,
                bgcolor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0
            }}
        >
            <Box sx={{ mt: 0 }}>
                <DashboardTabs tabs={dashboardTabs} onTabClick={handleTabClick} activeTab={null} />
            </Box>

            <Paper
                sx={{
                    p: { xs: 1.8, md: 2.2 },
                    bgcolor: '#FFFFFF',
                    borderRadius: '20px',
                    boxShadow: 'none',
                    flex: 1,
                    minHeight: 0
                }}
            >
                <Stack spacing={2.4}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        spacing={1.5}
                    >
                        <Box>
                            <Typography variant="h4" fontWeight={700} color="#3D3D3D">
                                Definições
                            </Typography>
                            <Typography variant="body2" color="#7A7A7A" mt={0.5}>
                                {activeSettingsTab === 'sos-locations'
                                    ? 'Gestão global das localizações e respetivos contactos SOS.'
                                    : activeSettingsTab === 'shared-accounts'
                                        ? 'Gestão dos emails base utilizados para contas partilhadas.'
                                        : 'Configuração global de alertas e notificações da plataforma.'}
                            </Typography>
                        </Box>

                        {activeSettingsTab === 'sos-locations' ? (
                            <Button
                                variant="contained"
                                startIcon={<AddCircleOutlineRoundedIcon />}
                                onClick={handleOpenCreateDialog}
                                sx={primaryButtonSx}
                            >
                                Nova localização
                            </Button>
                        ) : activeSettingsTab === 'shared-accounts' ? (
                            <Button
                                variant="contained"
                                startIcon={<AddCircleOutlineRoundedIcon />}
                                onClick={handleOpenSharedUserDialog}
                                sx={primaryButtonSx}
                            >
                                Nova conta partilhada
                            </Button>
                        ) : clinicalParametersEditing ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelClinicalParametersEdit}
                                    disabled={clinicalParametersSaving}
                                    sx={secondaryButtonSx}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveClinicalParameters}
                                    disabled={clinicalParametersSaving || clinicalParametersLoading}
                                    sx={primaryButtonSx}
                                >
                                    {clinicalParametersSaving ? 'A guardar...' : 'Guardar alterações'}
                                </Button>
                            </Stack>
                        ) : null}
                    </Stack>

                    {success ? (
                        <Alert
                            severity="success"
                            onClose={() => setSuccess('')}
                            sx={{ borderRadius: '14px' }}
                        >
                            {success}
                        </Alert>
                    ) : null}

                    {error ? (
                        <Alert severity="error" sx={{ borderRadius: '14px' }}>
                            {error}
                        </Alert>
                    ) : null}

                    <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
                        {settingsTabs.map((tab) => (
                            <Button
                                key={tab.key}
                                variant="outlined"
                                sx={activeSettingsTab === tab.key ? settingsTabActiveSx : settingsTabSx}
                                onClick={() => setActiveSettingsTab(tab.key)}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </Stack>

                    {activeSettingsTab === 'sos-locations' ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '320px minmax(0, 1fr)' },
                            gap: 2
                        }}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: '18px',
                                borderColor: '#E5E5E5',
                                bgcolor: '#FFFFFF'
                            }}
                        >
                            <Typography variant="h6" fontWeight={700} color="#3D3D3D" mb={1.6}>
                                Localizações
                            </Typography>

                            <Stack spacing={1.1}>
                                {loading ? (
                                    <Typography variant="body2" color="#7A7A7A">
                                        A carregar localizações...
                                    </Typography>
                                ) : locations.length === 0 ? (
                                    <Typography variant="body2" color="#7A7A7A">
                                        Ainda não existem localizações criadas.
                                    </Typography>
                                ) : (
                                    locations.map((item) => {
                                        const active = item.uuid === selectedLocationUuid;
                                        return (
                                            <Button
                                                key={item.uuid}
                                                variant="outlined"
                                                onClick={() => setSelectedLocationUuid(item.uuid)}
                                                sx={{
                                                    justifyContent: 'flex-start',
                                                    alignItems: 'flex-start',
                                                    textAlign: 'left',
                                                    p: 1.5,
                                                    borderRadius: '16px',
                                                    textTransform: 'none',
                                                    borderWidth: active ? '2px' : '1px',
                                                    borderColor: active ? '#D63731' : '#E3E3E3',
                                                    bgcolor: active ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
                                                    '&:hover': {
                                                        borderColor: '#D63731',
                                                        bgcolor: active
                                                            ? 'rgba(214, 55, 49, 0.05)'
                                                            : '#FAFAFA'
                                                    }
                                                }}
                                            >
                                                <Stack spacing={0.3} sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight={700}
                                                        color="#3D3D3D"
                                                    >
                                                        {item.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="#8A8A8A">
                                                        Atualizada em {item.updated?.slice(0, 10) || '--'}
                                                    </Typography>
                                                </Stack>
                                            </Button>
                                        );
                                    })
                                )}
                            </Stack>
                        </Paper>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2.2,
                                borderRadius: '18px',
                                borderColor: '#E5E5E5',
                                bgcolor: '#FFFFFF',
                                minHeight: 340
                            }}
                        >
                            {!selectedLocationUuid ? (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{ minHeight: 280 }}
                                >
                                    <Typography variant="body1" color="#7A7A7A">
                                        Seleciona uma localização para ver os contactos SOS.
                                    </Typography>
                                </Stack>
                            ) : (
                                <Stack spacing={2}>
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'stretch', md: 'center' }}
                                        spacing={1.2}
                                    >
                                        <Box>
                                            <Typography variant="h5" fontWeight={700} color="#3D3D3D">
                                                {selectedLocationSummary?.name || 'Localização'}
                                            </Typography>
                                            <Typography variant="body2" color="#8A8A8A" mt={0.5}>
                                                Contactos de emergência associados a esta localização.
                                            </Typography>
                                        </Box>

                                        <Button
                                            variant="outlined"
                                            startIcon={
                                                <Box
                                                    component="img"
                                                    src="/assets/logos-svg/editRed.svg"
                                                    alt="Editar"
                                                    sx={{ width: 16, height: 16, display: 'block' }}
                                                />
                                            }
                                            onClick={handleOpenEditDialog}
                                            disabled={!selectedLocationDetails || loadingDetails}
                                            sx={secondaryButtonSx}
                                        >
                                            Editar
                                        </Button>
                                    </Stack>

                                    {detailsError ? (
                                        <Alert severity="error" sx={{ borderRadius: '14px' }}>
                                            {detailsError}
                                        </Alert>
                                    ) : null}

                                    {loadingDetails ? (
                                        <Typography variant="body2" color="#7A7A7A">
                                            A carregar contactos SOS...
                                        </Typography>
                                    ) : Array.isArray(selectedLocationDetails?.contacts) &&
                                      selectedLocationDetails.contacts.length > 0 ? (
                                        <Stack spacing={1.2}>
                                            {selectedLocationDetails.contacts.map((contact, index) => (
                                                <Paper
                                                    key={`${contact.name || 'contact'}-${contact.phone || index}`}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1.6,
                                                        borderRadius: '16px',
                                                        borderColor: '#E5E5E5',
                                                        bgcolor: '#FAFAFA'
                                                    }}
                                                >
                                                    <Typography variant="body1" fontWeight={700} color="#3D3D3D">
                                                        {contact.name || 'Sem nome'}
                                                    </Typography>
                                                    <Typography variant="body2" color="#6F6F6F" mt={0.5}>
                                                        {contact.phone || 'Sem contacto telefÃ³nico'}
                                                    </Typography>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <TextField
                                            label="Contactos SOS"
                                            fullWidth
                                            multiline
                                            minRows={12}
                                            value={selectedLocationDetails?.content || ''}
                                            disabled
                                            InputProps={{ readOnly: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '18px',
                                                    bgcolor: '#FAFAFA'
                                                }
                                            }}
                                        />
                                    )}
                                </Stack>
                            )}
                        </Paper>
                    </Box>
                    ) : activeSettingsTab === 'shared-accounts' ? (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '360px minmax(0, 1fr)' },
                                gap: 2
                            }}
                        >
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: '18px',
                                    borderColor: '#E5E5E5',
                                    bgcolor: '#FFFFFF'
                                }}
                            >
                                <Typography variant="h6" fontWeight={700} color="#3D3D3D" mb={1.6}>
                                    Emails partilhados
                                </Typography>

                                {sharedUsersError ? (
                                    <Alert severity="error" sx={{ borderRadius: '14px', mb: 1.5 }}>
                                        {sharedUsersError}
                                    </Alert>
                                ) : null}

                                <Stack spacing={1.1}>
                                    {sharedUsersLoading ? (
                                        <Typography variant="body2" color="#7A7A7A">
                                            A carregar contas partilhadas...
                                        </Typography>
                                    ) : sharedUsers.length === 0 ? (
                                        <Typography variant="body2" color="#7A7A7A">
                                            Ainda não existem emails partilhados criados.
                                        </Typography>
                                    ) : (
                                        sharedUsers.map((item) => (
                                            <Paper
                                                key={item.uuid}
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: '16px',
                                                    borderColor: '#E5E5E5',
                                                    bgcolor: '#FFFFFF',
                                                    boxShadow: 'none'
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1.2}
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            variant="body1"
                                                            fontWeight={700}
                                                            color="#3D3D3D"
                                                        >
                                                            {item.email}
                                                        </Typography>
                                                    </Box>

                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => handleOpenDeleteSharedUserDialog(item)}
                                                        sx={secondaryButtonSx}
                                                    >
                                                        Desativar
                                                    </Button>
                                                </Stack>
                                            </Paper>
                                        ))
                                    )}
                                </Stack>
                            </Paper>

                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2.2,
                                    borderRadius: '18px',
                                    borderColor: '#E5E5E5',
                                    bgcolor: '#FFFFFF',
                                    minHeight: 320
                                }}
                            >
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="h5" fontWeight={700} color="#3D3D3D">
                                            Como funciona
                                        </Typography>
                                        <Typography variant="body2" color="#8A8A8A" mt={0.5}>
                                            Estes emails servem como base para criar cuidadores com conta
                                            partilhada na página de criação de cuidadores.
                                        </Typography>
                                    </Box>

                                    <TextField
                                        label="Resumo"
                                        fullWidth
                                        multiline
                                        minRows={10}
                                        value={
                                            sharedUsers.length
                                                ? sharedUsers
                                                      .map((item, index) => `${index + 1}. ${item.email}`)
                                                      .join('\n')
                                                : 'Sem contas partilhadas configuradas.'
                                        }
                                        disabled
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '18px',
                                                bgcolor: '#FAFAFA'
                                            }
                                        }}
                                    />
                                </Stack>
                            </Paper>
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            <Alert
                                severity={clinicalParametersEditing ? 'warning' : 'info'}
                                sx={{
                                    borderRadius: '16px',
                                    bgcolor: '#F7F7F7',
                                    color: '#3D3D3D',
                                    '& .MuiAlert-icon': {
                                        color: '#D63731'
                                    }
                                }}
                            >
                                {clinicalParametersEditing
                                    ? 'Está a editar os limites clínicos globais da conta. Estas alterações afetam a geração automática de alertas.'
                                    : 'Os limites abaixo são utilizados para gerar alertas automáticos a partir dos registos biométricos.'}
                            </Alert>

                            {clinicalParametersError ? (
                                <Alert severity="error" sx={{ borderRadius: '16px' }}>
                                    {clinicalParametersError}
                                </Alert>
                            ) : null}

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.6fr) minmax(320px, 0.9fr)' },
                                    gap: 2
                                }}
                            >
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2.2,
                                        borderRadius: '18px',
                                        borderColor: '#E5E5E5',
                                        bgcolor: '#FFFFFF'
                                    }}
                                >
                                        <Stack
                                            direction={{ xs: 'column', md: 'row' }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: 'flex-start', md: 'center' }}
                                            spacing={1}
                                            sx={{ mb: 2 }}
                                        >
                                            <Stack direction="row" spacing={1.2} alignItems="center">
                                                <Typography variant="h6" fontWeight={700} color="#3D3D3D">
                                            Alertas biométricos ativos
                                                </Typography>
                                                {!clinicalParametersEditing ? (
                                                    <Fab
                                                        size="small"
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            minHeight: 30,
                                                            bgcolor: '#D63731',
                                                            color: '#FFFFFF',
                                                            boxShadow: 'none',
                                                            '&:hover': {
                                                                bgcolor: '#BF2F2A',
                                                                boxShadow: 'none'
                                                            }
                                                        }}
                                                        onClick={handleEnableClinicalParametersEdit}
                                                        disabled={clinicalParametersLoading}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src="/assets/logos-svg/edit.svg"
                                                            alt="Editar limites"
                                                            sx={{ width: 16, height: 16, display: 'block' }}
                                                        />
                                                    </Fab>
                                                ) : null}
                                            </Stack>
                                            <Typography variant="body2" color="#7A7A7A">
                                            Última atualização:{' '}
                                                {clinicalParametersLastUpdate
                                                    ? new Date(clinicalParametersLastUpdate).toLocaleString('pt-PT')
                                                    : 'Sem registo'}
                                            </Typography>
                                        </Stack>

                                    {clinicalParametersLoading ? (
                                        <Typography variant="body2" color="#7A7A7A">
                                            A carregar parâmetros clínicos...
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1.6}>
                                            {biometricAlertRules.map((rule) => (
                                                <Paper
                                                    key={rule.key}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1.8,
                                                        borderRadius: '18px',
                                                        borderColor: '#E8E8E8',
                                                        bgcolor: '#FCFCFC',
                                                        boxShadow: 'none'
                                                    }}
                                                >
                                                    <Stack spacing={1.4}>
                                                        <Stack
                                                            direction={{ xs: 'column', md: 'row' }}
                                                            justifyContent="space-between"
                                                            alignItems={{ xs: 'flex-start', md: 'center' }}
                                                            spacing={1}
                                                        >
                                                            <Box>
                                                                <Typography variant="h6" fontWeight={700} color="#3D3D3D">
                                                                    {rule.title}
                                                                </Typography>
                                                                <Typography variant="body2" color="#7A7A7A" mt={0.4}>
                                                                    {rule.description}
                                                                </Typography>
                                                            </Box>

                                                            <Box
                                                                sx={{
                                                                    px: 1.4,
                                                                    py: 0.65,
                                                                    borderRadius: '999px',
                                                                    bgcolor: 'rgba(214, 55, 49, 0.08)',
                                                                    color: '#D63731',
                                                                    fontWeight: 700,
                                                                    fontSize: 13,
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                Prioridade {rule.priority}
                                                            </Box>
                                                        </Stack>

                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                                                gap: 1.3
                                                            }}
                                                        >
                                                            {rule.conditions.map((condition) => {
                                                                const formValue = condition.path.reduce(
                                                                    (acc, key) => acc?.[key],
                                                                    clinicalParametersForm
                                                                );

                                                                return (
                                                                    <TextField
                                                                        key={`${rule.key}-${condition.label}`}
                                                                        label={condition.label}
                                                                        value={formValue || ''}
                                                                        fullWidth
                                                                        disabled={!clinicalParametersEditing}
                                                                        onChange={(event) =>
                                                                            handleClinicalParameterFieldChange(
                                                                                condition.path,
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                        InputProps={{
                                                                            readOnly: !clinicalParametersEditing,
                                                                            endAdornment: (
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    color="#7A7A7A"
                                                                                    sx={{ mr: 1 }}
                                                                                >
                                                                                    {condition.suffix}
                                                                                </Typography>
                                                                            )
                                                                        }}
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': {
                                                                                borderRadius: '14px',
                                                                                bgcolor: clinicalParametersEditing ? '#FFFFFF' : '#FAFAFA'
                                                                            }
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    </Stack>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    )}
                                </Paper>

                                <Stack spacing={2}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2.2,
                                            borderRadius: '18px',
                                            borderColor: '#E5E5E5',
                                            bgcolor: '#FFFFFF'
                                        }}
                                    >
                                        <Typography variant="h6" fontWeight={700} color="#3D3D3D" mb={1.6}>
                                            Encaminhamento atual
                                        </Typography>

                                        <Stack spacing={1.2}>
                                            <TextField
                                                label="Profissional alvo"
                                                value="Enfermagem"
                                                fullWidth
                                                disabled
                                                InputProps={{ readOnly: true }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '14px',
                                                        bgcolor: '#FFFFFF'
                                                    }
                                                }}
                                            />
                                            <TextField
                                                label="Regra de seleção"
                                                value="Maior prioridade de chamada e atividade recente"
                                                fullWidth
                                                disabled
                                                InputProps={{ readOnly: true }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '14px',
                                                        bgcolor: '#FFFFFF'
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2" color="#7A7A7A">
                                                Quando existe alerta, o backend procura primeiro um profissional de
                                                Enfermagem disponível na conta e ordena por prioridade de chamada e
                                                último ping de atividade.
                                            </Typography>
                                        </Stack>
                                    </Paper>

                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2.2,
                                            borderRadius: '18px',
                                            borderColor: '#E5E5E5',
                                            bgcolor: '#FFFFFF'
                                        }}
                                    >
                                        <Typography variant="h6" fontWeight={700} color="#3D3D3D" mb={1.6}>
                                            Sem alertas automáticos
                                        </Typography>

                                        <Stack spacing={1.1}>
                                            {nonAlertMeasurements.map((item) => (
                                                <Box
                                                    key={item}
                                                    sx={{
                                                        px: 1.4,
                                                        py: 1.2,
                                                        borderRadius: '14px',
                                                        bgcolor: '#F7F7F7',
                                                        color: '#5F5F5F',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {item}
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Paper>

                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2.2,
                                            borderRadius: '18px',
                                            borderColor: '#E5E5E5',
                                            bgcolor: '#FFFFFF'
                                        }}
                                    >
                                        <Typography variant="h6" fontWeight={700} color="#3D3D3D" mb={1.2}>
                                            Observações
                                        </Typography>
                                        <Typography variant="body2" color="#7A7A7A">
                                            Estes limites são aplicados ao nível da conta e influenciam a criação de
                                            notificações automáticas para toda a equipa clínica.
                                        </Typography>
                                    </Paper>
                                </Stack>
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </Paper>

            <Dialog
                open={sharedUserDialogOpen}
                onClose={handleCloseSharedUserDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Nova conta partilhada</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    {sharedUserDialogError ? <Alert severity="error">{sharedUserDialogError}</Alert> : null}
                    <TextField
                        label="Email da conta partilhada"
                        fullWidth
                        value={sharedUserEmail}
                        onChange={(event) => setSharedUserEmail(event.target.value)}
                        disabled={sharedUserSubmitting}
                        helperText="Este email passa a estar disponível para ser usado nos cuidadores com conta partilhada."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '14px'
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        variant="outlined"
                        onClick={handleCloseSharedUserDialog}
                        disabled={sharedUserSubmitting}
                        sx={secondaryButtonSx}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateSharedUser}
                        disabled={sharedUserSubmitting}
                        sx={primaryButtonSx}
                    >
                        {sharedUserSubmitting ? 'A guardar...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(sharedUserDeleteTarget)}
                onClose={handleCloseDeleteSharedUserDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Desativar conta partilhada</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Typography variant="body1" color="#3D3D3D">
                        Tem a certeza que quer desativar a conta partilhada{' '}
                        <strong>{sharedUserDeleteTarget?.email || '--'}</strong>?
                    </Typography>
                    <Typography variant="body2" color="#7A7A7A">
                        Esta ação só vai resultar se essa conta já não tiver roles ativos associados.
                    </Typography>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        variant="outlined"
                        onClick={handleCloseDeleteSharedUserDialog}
                        disabled={sharedUserDeleting}
                        sx={secondaryButtonSx}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteSharedUser}
                        disabled={sharedUserDeleting}
                        sx={primaryButtonSx}
                    >
                        {sharedUserDeleting ? 'A desativar...' : 'Desativar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>
                    {dialogMode === 'create' ? 'Nova localização' : 'Editar localização'}
                </DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    {dialogError ? <Alert severity="error">{dialogError}</Alert> : null}
                    <TextField
                        label="Nome da localização"
                        fullWidth
                        value={dialogForm.name}
                        onChange={(event) =>
                            setDialogForm((prev) => ({
                                ...prev,
                                name: event.target.value
                            }))
                        }
                        disabled={dialogSubmitting}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '14px'
                            }
                        }}
                    />
                    {dialogForm.isLegacyText ? (
                        <TextField
                            label="Contactos SOS"
                            fullWidth
                            multiline
                            minRows={10}
                            value={dialogForm.content}
                            onChange={(event) =>
                                setDialogForm((prev) => ({
                                    ...prev,
                                    content: event.target.value
                                }))
                            }
                            disabled={dialogSubmitting}
                        helperText="Pode inserir os contactos de emergência em texto livre."
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '14px'
                                }
                            }}
                        />
                    ) : (
                        <Stack spacing={1.4}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700} color="#3D3D3D">
                                        Contactos SOS
                                    </Typography>
                                    <Typography variant="body2" color="#7A7A7A">
                                        Adiciona um ou mais contactos com nome e telefone.
                                    </Typography>
                                </Box>

                                <Button
                                    variant="outlined"
                                    startIcon={<AddCircleOutlineRoundedIcon />}
                                    onClick={handleAddLocationContact}
                                    disabled={dialogSubmitting}
                                    sx={secondaryButtonSx}
                                >
                                    Adicionar
                                </Button>
                            </Stack>

                            <Stack spacing={1.2}>
                                {dialogForm.contacts.map((contact, index) => (
                                    <Paper
                                        key={`contact-row-${index}`}
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            borderRadius: '16px',
                                            borderColor: '#E5E5E5',
                                            bgcolor: '#FAFAFA'
                                        }}
                                    >
                                        <Stack spacing={1.2}>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                                                    Contacto {index + 1}
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    color="inherit"
                                                    onClick={() => handleRemoveLocationContact(index)}
                                                    disabled={dialogSubmitting || dialogForm.contacts.length === 1}
                                                    sx={{
                                                        minWidth: 'auto',
                                                        p: 0,
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        color: '#7A7A7A',
                                                        '&:hover': {
                                                            bgcolor: 'transparent',
                                                            color: '#3D3D3D'
                                                        }
                                                    }}
                                                >
                                                    Remover
                                                </Button>
                                            </Stack>

                                            <Stack
                                                direction={{ xs: 'column', md: 'row' }}
                                                spacing={1.2}
                                            >
                                                <TextField
                                                    label="Nome"
                                                    fullWidth
                                                    value={contact.name}
                                                    onChange={(event) =>
                                                        handleLocationContactChange(index, 'name', event.target.value)
                                                    }
                                                    disabled={dialogSubmitting}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '14px',
                                                            bgcolor: '#FFFFFF'
                                                        }
                                                    }}
                                                />
                                                <TextField
                                                    label="Telefone"
                                                    fullWidth
                                                    value={contact.phone}
                                                    onChange={(event) =>
                                                        handleLocationContactChange(index, 'phone', event.target.value)
                                                    }
                                                    disabled={dialogSubmitting}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '14px',
                                                            bgcolor: '#FFFFFF'
                                                        }
                                                    }}
                                                />
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        variant="outlined"
                        onClick={handleCloseDialog}
                        disabled={dialogSubmitting}
                        sx={secondaryButtonSx}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveDialog}
                        disabled={dialogSubmitting}
                        sx={primaryButtonSx}
                    >
                        {dialogSubmitting ? 'A guardar...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Settings;


